import {
    DEFAULT_SECTION_ORDER,
    type DomainMovieConfiguration,
    type ElementOffset,
    HiddenSections,
    PageSectionId
} from '../premium-movie-landing-page-configuration/PremiumMovieLandingPageConfigurationDto';

/**
 * The stored order, cleaned: unknown ids and duplicates dropped, missing sections appended in
 * default order. No stored order, or a stored value that is not a list, yields today's order.
 */
export function normalizeSectionOrder(stored: unknown): PageSectionId[] {
    const known = new Set<string>(DEFAULT_SECTION_ORDER);
    const seen = new Set<string>();
    const order: PageSectionId[] = [];
    for (const id of Array.isArray(stored) ? stored : []) {
        if (known.has(id) && !seen.has(id)) {
            seen.add(id);
            order.push(id as PageSectionId);
        }
    }
    return [...order, ...DEFAULT_SECTION_ORDER.filter((id) => !seen.has(id))];
}

/** A section's hide switch. The trailer's is read by `useTrailer`, which also feeds the hero. */
export const SECTION_HIDDEN_VALUE: Record<PageSectionId, HiddenSections> = {
    [PageSectionId.TRAILER]: HiddenSections.TRAILER,
    [PageSectionId.WATCH_MORE]: HiddenSections.WATCH_MORE,
    [PageSectionId.IMAGES_GALLERY]: HiddenSections.IMAGES_GALLERY,
    [PageSectionId.GRETA]: HiddenSections.GRETA,
    [PageSectionId.HOME_ENTERTAINMENT]: HiddenSections.HOME_ENTERTAINMENT,
    [PageSectionId.PRESS_QUOTES]: HiddenSections.PRESS_QUOTES,
    [PageSectionId.CINEMA_TOUR]: HiddenSections.CINEMA_TOUR,
    [PageSectionId.COMPETITION]: HiddenSections.COMPETITION,
    [PageSectionId.DETAILS]: HiddenSections.DETAILS,
    [PageSectionId.PARTNERS]: HiddenSections.PARTNERS
};

/** Whether the configuration hides `section`. A stored value that is not a list hides nothing. */
export function isSectionHidden(
    config: Pick<DomainMovieConfiguration, 'hiddenSections'>,
    section: HiddenSections
): boolean {
    return Array.isArray(config.hiddenSections) && config.hiddenSections.includes(section);
}

/** Nudge bounds of a hero element, per breakpoint. The API clamps non-staff writes to the same ranges. */
export const OFFSET_LIMIT = 400;
export const SCALE_MIN = 0.5;
export const SCALE_MAX = 2;

/** NaN and Infinity (a broken pointer event) fall back to the neutral value instead of poisoning the config. */
const clamp = (value: unknown, min: number, max: number, fallback: number): number =>
    typeof value === 'number' && Number.isFinite(value) ? Math.min(max, Math.max(min, value)) : fallback;

/** An offset forced into its bounds and rounded (px whole, scale to 0.01); a missing scale is 1. */
export function clampOffset(offset: ElementOffset): ElementOffset {
    return {
        dx: Math.round(clamp(offset.dx, -OFFSET_LIMIT, OFFSET_LIMIT, 0)),
        dy: Math.round(clamp(offset.dy, -OFFSET_LIMIT, OFFSET_LIMIT, 0)),
        scale: Math.round(clamp(offset.scale, SCALE_MIN, SCALE_MAX, 1) * 100) / 100
    };
}
